import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { toastError, toastSuccess } from '$/store/toast/actions';

import { ReactComponent as PencilIcon } from '$/assets/icons/penciledit.svg';
import { AddNote } from '$/components/add-note';
import Loading from '$/components/loading/Loading';
import { AnalystNote } from '$/core/models';
import { CompanyService } from '$/services';
import * as Bixby from '$/core';
import './index.scss';
import BixButton from '$/components/global/bixButton';

interface IAnalystNotesProps {
  currentCompany: Bixby.Models.AdminCompany;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
}

const AnalystNotes: React.FC<IAnalystNotesProps> = props => {
  const { currentCompany, dispatchToastError, dispatchToastSuccess } = props;

  const [isLoadingAnalystNotes, setIsLoadingAnalystNotes] = useState(false);
  const [analystNotes, setAnalystNotes] = useState<Bixby.Models.AnalystNote[]>([]);
  const [isCreatingAnalystNote, setIsCreatingAnalystNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const getAnalystNotes = useCallback(
    async (companyId: string) => {
      try {
        setIsLoadingAnalystNotes(true);
        const analystNotes = await CompanyService.getAnalystNotes(companyId);
        setAnalystNotes(analystNotes);
      } catch (error: any) {
        dispatchToastError(`Failed to retrieve Analyst Notes`);
      } finally {
        setIsLoadingAnalystNotes(false);
      }
    },
    [dispatchToastError]
  );

  useEffect(() => {
    getAnalystNotes(currentCompany.id);
  }, [getAnalystNotes, currentCompany.id]);

  const handleAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsCreatingAnalystNote(true);
  };

  const handleModalDismiss = () => {
    setIsCreatingAnalystNote(false);
  };

  const handleCreateAnalystNote = async (companyId: string, note: string) => {
    try {
      const analystNotes = await CompanyService.createAnalystNote(companyId, {
        note: note,
      } as Bixby.Models.CreateNote);

      setAnalystNotes(analystNotes);
      dispatchToastSuccess(`Analyst Note created`);
    } catch (error: any) {
      dispatchToastError(`Analyst Note failed to be created`);
    } finally {
      setIsCreatingAnalystNote(false);
    }
  };

  const handleStartEdit = (noteId: string, noteText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(noteText);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleUpdateAnalystNote = async (analystNoteId: string) => {
    try {
      await CompanyService.updateAnalystNote(currentCompany.id, analystNoteId, {
        id: analystNoteId,
        note: editingNoteText,
      });
      setEditingNoteId(null);
      setEditingNoteText('');
      await getAnalystNotes(currentCompany.id);
    } catch (error) {
      dispatchToastError(`Analyst Note failed to be updated`);
    }
  };

  const handleDeleteAnalystNote = async (analystNoteId: string) => {
    try {
      await CompanyService.deleteAnalystNote(currentCompany.id, analystNoteId);
      await getAnalystNotes(currentCompany.id);
    } catch (error) {
      dispatchToastError(`Analyst Note failed to be deleted`);
    }
  };

  const tableRows = Bixby.Utils.orderBy(analystNotes || [], c => c.createdDate, 'desc').map((row, index) => {
    const rowTyped = row as AnalystNote;
    return (
      <tr key={index}>
        <td>
          <div className="analyst_notes_entry">
            <p className="note_byline">
              {rowTyped.createdBy} ({Bixby.Utils.formatByLineDateTime(rowTyped.createdDate)}):
            </p>
            <div className="note_content">
              {editingNoteId === rowTyped.analystNoteId ? (
                <>
                  <textarea
                    value={editingNoteText}
                    onChange={e => setEditingNoteText(e.target.value)}
                    className="note edit_textarea"
                  />
                  <div className="edit_actions">
                    <BixButton
                      useSmall
                      onClick={() => handleUpdateAnalystNote(rowTyped.analystNoteId)}
                      className="save_btn">
                      Save
                    </BixButton>
                    <BixButton useSmall onClick={handleCancelEdit} className="cancel_btn">
                      Cancel
                    </BixButton>
                  </div>
                </>
              ) : (
                <>
                  <span className="note">{rowTyped.note}</span>
                  <div>
                    <button
                      onClick={() => handleStartEdit(rowTyped.analystNoteId, rowTyped.note)}
                      className="edit_icon">
                      <PencilIcon />
                    </button>
                    <button
                      className="fa fa-minus-circle delete_icon"
                      onClick={() => handleDeleteAnalystNote(rowTyped.analystNoteId)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
      </tr>
    );
  });

  return (
    <React.Fragment>
      <div className="analyst_notes_wrap">
        <table className="table analyst_notes_table">
          <tbody>
            <tr>
              <td>
                <button type="button" className="add_btn" onClick={handleAddModal}>
                  + Add Note
                </button>
                <AddNote
                  active={isCreatingAnalystNote}
                  clientCompanyId={currentCompany.id}
                  onAddSubmit={handleCreateAnalystNote}
                  onCloseModal={handleModalDismiss}
                  title="Add New Analyst Note"
                />
              </td>
            </tr>
            {isLoadingAnalystNotes && (
              <tr className="loading">
                <td>
                  <Loading inline />
                </td>
              </tr>
            )}
            {!isLoadingAnalystNotes && tableRows}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AnalystNotes);
